import type { BoardState, GameSnapshot } from './types';
import { TARGET_SCORE } from './types';
import { SeededRNG } from './rng';
import {
  createBoard,
  cloneBoard,
  removeGroup,
  applyGravity,
  compactLeft,
  hasMoves,
  findGroup,
  isGridEmpty,
} from './board';

export class GameState {
  private rng: SeededRNG;
  private board: BoardState;
  private bestScore: number;
  private undoSnapshot: GameSnapshot | null = null;
  private initialSeed: number;

  constructor(seed: number = Date.now(), bestScore: number = 0) {
    this.initialSeed = seed;
    this.rng = new SeededRNG(seed);
    this.board = createBoard(this.rng);
    this.bestScore = bestScore;
  }

  /**
   * Get current board state
   */
  getBoard(): BoardState {
    return this.board;
  }

  /**
   * Get current score
   */
  getScore(): number {
    return this.board.score;
  }

  /**
   * Get best score
   */
  getBestScore(): number {
    return this.bestScore;
  }

  /**
   * Update best score
   */
  updateBestScore(): void {
    if (this.board.score > this.bestScore) {
      this.bestScore = this.board.score;
    }
  }

  /**
   * Check if target score has been reached
   */
  hasReachedTarget(): boolean {
    return this.board.score >= TARGET_SCORE;
  }

  /**
   * Check if there are moves remaining
   */
  hasMovesRemaining(): boolean {
    return hasMoves(this.board.grid);
  }

  /**
   * Check if the grid is completely empty
   */
  isGridEmpty(): boolean {
    return isGridEmpty(this.board.grid);
  }

  /**
   * Handle a tile click/tap at position (x, y)
   * Returns the positions of removed tiles, or null if no group was removed
   */
  handleClick(x: number, y: number): Array<{ x: number; y: number }> | null {
    // Find the group first
    const group = findGroup(this.board.grid, x, y);

    if (group.length < 2) {
      return null;
    }

    // Save undo snapshot before making a move
    this.undoSnapshot = {
      board: cloneBoard(this.board),
      score: this.board.score,
    };

    // Remove the group (this updates the score but doesn't apply gravity yet)
    const removed = removeGroup(this.board, x, y);

    if (removed) {
      this.updateBestScore();
      return group;
    }

    // If nothing was removed, clear undo snapshot
    this.undoSnapshot = null;
    return null;
  }

  /**
   * Apply gravity and compaction after animations complete
   */
  applyGravity(): void {
    applyGravity(this.board.grid);
    compactLeft(this.board.grid);
  }

  /**
   * Undo the last move
   */
  undo(): boolean {
    if (!this.undoSnapshot) return false;

    this.board = cloneBoard(this.undoSnapshot.board);
    this.undoSnapshot = null;
    return true;
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoSnapshot !== null;
  }

  /**
   * Restart game with current seed
   */
  restart(newSeed?: number): void {
    if (newSeed !== undefined) {
      this.rng.setSeed(newSeed);
    }
    this.board = createBoard(this.rng);
    this.undoSnapshot = null;
  }

  /**
   * Continue with a fresh board (after reaching target)
   */
  continue(newSeed?: number): void {
    const currentScore = this.board.score;
    if (newSeed !== undefined) {
      this.rng.setSeed(newSeed);
    }
    this.board = createBoard(this.rng);
    this.board.score = currentScore; // Keep the score
    this.undoSnapshot = null;
  }

  /**
   * Get initial RNG seed for debugging/testing
   */
  getSeed(): number {
    return this.initialSeed;
  }
}
